import { ActionCreator, AjaxDispatcher } from '../ajax-dispatcher'
import ActiveFilters, { FilterActiveValues } from './active-filters';
import FilterBase from './filter-base';
import FilterParamStats from './filter-stats';
import { getComponentPart, getCurrentLanguageId, loadAsyncComponent, toggleClass } from '../common';
import HttpClient from '../http-client';
import { initFilters } from '../main';
import $ from "jquery";

class FilterComponent {
    type: string;
    id: string;
    queryValues: string;

    constructor(type: string, id: string, queryValues: string) {
        this.type = type;
        this.id = id;
        this.queryValues = queryValues;
    }
}

export default class FilterBox {
    queryParams: { [key: string]: any } = {};
    private activeFilters: ActiveFilters;
    private registeredFilters: FilterBase[] = [];
    private baseElement: HTMLElement;
    private toggleContainerButtonElement: HTMLElement;
    private filterContainerElement: HTMLElement;
    private _pageId: number;

    get pageId(): number{
        return this._pageId;
    }

    async init(): Promise<void> {
        if (this.hasUrlQueryString()) {
            this.queryParams = this.getFiltersQueryParamsFromUrl();
        }
        this.baseElement = await this.load();
        this.activeFilters = new ActiveFilters(() => this.clearAll());
        this._pageId = this.getPageId();
        initFilters();
        this.initToggleButton();
        this.initToggleFilters();
    }
    
    private initToggleButton(): void {
        this.toggleContainerButtonElement = getComponentPart(this.baseElement, 'toggle-button');
        this.filterContainerElement = getComponentPart(this.baseElement, 'filter-container');
        this.toggleContainerButtonElement.addEventListener('click', () => this.toggleFiltersContainer())
    }

    private toggleFiltersContainer(): void {
        toggleClass(this.toggleContainerButtonElement, 'open');
        $(this.filterContainerElement).slideToggle('fast');
    }

    private initToggleFilters(): void {
        $('.filter-component__heading').click(function () {
            $(this).toggleClass('open');
            $(this).parent().children('.filter-component__group').slideToggle('fast');
        });
    }

    private async load(): Promise<HTMLElement> {
        const placeholderElement = document.querySelector('[data-component="FilterBox"]') as HTMLElement;
        const loadedElement = await loadAsyncComponent(placeholderElement);
        return loadedElement;
    }

    register(filter: FilterBase) {
        this.registeredFilters.push(filter);
    }

    private getPageId(): number{
        return Number(this.baseElement.dataset.pageId);
    }

    private clearAll(): void {
        this.registeredFilters.forEach(f => {
            f.clearValues();
            this.clearActiveValues(f.paramId);
        });
        this.refreshFiltersAndProducts();
    }

    getInitialFilterValues(filterType: string): { [key: string]: any } {
        const filters = this.getFilterComponents().filter(f => f.type === filterType);
        let initialFilters: { [key: string]: any } = {};

        filters.forEach(f => {
            initialFilters[f.id] = this.queryParams[f.id];
        });

        return initialFilters;
    }

    private hasUrlQueryString(): boolean {
        const currentUrl = window.location.href;
        const pattern = new RegExp(/\?+/g);
        return pattern.test(currentUrl);
    }

    private getFilterComponents(): FilterComponent[] {
        const elements = [...document.querySelectorAll('[data-component-type="filter-component"]')];

        const filters = elements.map(element =>
            new FilterComponent(
                element.getAttribute('data-component'),
                element.getAttribute('data-param-id'),
                element.getAttribute('data-selected-values')));

        return filters;
    }

    private getFiltersQueryString(filters: FilterComponent[]): string {
        let params: { [key: string]: any } = {};

        filters.forEach(filter => {
            if (filter.queryValues) {
                params[filter.id] = filter.queryValues;
            }
        });

        return this.buildQueryString(params);
    }

    private buildQueryString(params: { [x: string]: any; }): string {
        const paramString = 'param';
        let query = Object.keys(params)
            .map(k => paramString + k + '=' + params[k])
            .join('&');

        query = this.appendOrdering(query);

        return query;
    }

    private appendOrdering(query: string): string {
        const ordering = this.getOrdering();
        if (ordering) {
            query += query !== '' ? '&' : '';
            query += `order=${ordering}`;
        }
        return query;
    }

    private getOrdering(): string {
        const currentParams = this.getQueryParams();
        return currentParams['order'];
    }

    private getQueryParams(): { [key: string]: any; } {
        const paramPart: string = window.location.search.replace('?', '');
        const paramSplit: string[] = paramPart ? paramPart.split('&') : [];
        const params: { [key: string]: any; } = {};
        paramSplit.map(p => p.split('=')).forEach(kv => params[kv[0]] = kv[1]);
        return params;
    }

    private getFiltersQueryParamsFromUrl(): { [key: string]: any; } {
        const paramPart: string = window.location.search.replace('?', '');
        return this.parseFilterParamsFromQueryString(paramPart);
    }

    private parseFilterParamsFromQueryString(queryString: string): { [key: string]: any; } {
        const paramSplit: string[] = queryString ? queryString.split('&') : [];
        const params: { [key: string]: any; } = {};
        const paramString: string = "param";

        paramSplit.map(p => p.split('=')).forEach(kv => {
            if (kv[0].includes(paramString)) {
                params[kv[0].split(paramString)[1]] = kv[1];
            }
        });

        return params;
    }

    refreshFiltersAndProducts(): void {
        const queryString = this.getFiltersQueryString(this.getFilterComponents());
        this.reloadProductList(queryString);
        this.refreshParamStats(queryString);
    }

    private getParamIds(): string {
        const ids = this.getFilterComponents().map(f => f.id);
        return ids.join(',');
    }

    private reloadProductList(queryString: string): void {
        const action = new ActionCreator().reloadProductListAction(queryString);
        new AjaxDispatcher().dispatchGetAction(action);
    }

    async refreshParamStats(queryString: string): Promise<void> {
        let url = `/filters/GetListItemsForParams?${queryString}`;
        url += queryString ? '&' : '';
        url += `paramIds=${this.getParamIds()}`;
        const response = await new HttpClient().fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId(),
                'PageId': this.pageId.toString()
            }
        });

        const stats: FilterParamStats[] = JSON.parse(await response.json());

        this.updateFilterStats(stats);
    }

    updateFilterStats(stats: FilterParamStats[]) {
        if (!stats) {
            return;
        }

        stats.forEach(s => {
            const filter = this.registeredFilters.find(f => f.paramId === s.ParamId);
            if (filter) {
                filter.setStats(s);
            }
        })
    }

    setActiveFilterValues(filterActiveValues: FilterActiveValues): void {
        this.activeFilters.setActiveValues(filterActiveValues);
    }

    clearActiveValues(paramId: number): void {
        this.activeFilters.clearActiveValues(paramId);
    }
}
